<script lang="ts">
    import {AlertCircle, Lightbulb, Search, Star} from '@lucide/svelte';
    import type {FormData, ToggleList, UpdateField} from '../../form/types';
    import Checkbox from "../common/Checkbox.svelte";
    import Button from "../common/Button.svelte";
    import Input from "../common/Input.svelte";
    import {slide} from "svelte/transition";
    import {FormAttributesStore} from "../../form/attributes";

    let {
        formData = $bindable(),
        valid = $bindable(true),
        updateField,
        toggleList,
    }: {
        formData: FormData;
        valid?: boolean;
        updateField: UpdateField;
        toggleList: ToggleList;
    } = $props();

    $effect(() => {

    });

    $effect(() => {
        const expertiseLength = formData.expertise.length
        if (expertiseLength === 0) {
            valid = true;
            return;
        }

        // check if every expertise has a level assigned
        const has_levels = formData.expertise.every(e => {
            return formData.expertiseLevels[e] !== undefined;
        })

        if (!has_levels) {
            valid = false;
            return;
        }

        // get all fields which have expert
        const expertExpertises = formData.expertise.filter(d => {
            return formData.expertiseLevels[d] === 'EXPERT';
        });

        let expertFieldsValid = true;

        // check if all expert fields have their required fields filled.
        for (const ex of expertExpertises) {
            const hasFilledEducation = !!formData.expertiseEducation[ex];
            const hasFilledYears = !!formData.expertiseYears[ex];
            if (!hasFilledEducation || !hasFilledYears) {
                expertFieldsValid = false;
            }
        }

        if (!expertFieldsValid) {
            valid = false;
            return;
        }

        if (formData.expertise.includes(OTHER_EXPERTISE) && !formData.otherExpertiseText.trim()) {
            valid = false;
            return;
        }

        const top3Length = formData.top3Expertise.length;


        valid = Math.min(expertiseLength, 3) === top3Length;
    });

    let searchTerm = $state('');

    const readInputValue = (event: Event): string =>
        (event.currentTarget as HTMLInputElement).value;

    const OTHER_EXPERTISE = 'Anders...';
    const levels = ['BASIS', 'GERICHT', 'EXPERT'] as const;

    const getExpertiseLabel = (theme: string): string =>
        theme === OTHER_EXPERTISE ? formData.otherExpertiseText || OTHER_EXPERTISE : theme;
</script>

<div class="space-y-4 animate-in fade-in duration-500"

>
    <h2 class="flex items-center gap-2 text-xl font-bold text-slate-800">
        <Lightbulb size={24} color="#7C46AF"/>
        Expertise & Specialisatie
    </h2>
    <p class="text-sm ">
        Om jou met zelfvertrouwen de juiste zwaarte aan casuïstiek op te laten pakken, willen we graag meer weten over
        je ervaringsniveau. Selecteer de thema's en expertisegebieden waar je ervaring mee hebt. Geef per thema aan hoe
        ervaren je bent. De optie 'expert' vervalt voor de meeste thematieken in categorie 1.
        <b>LET OP</b>: Geef hier alleen de thematieken aan die je voor SpecialistenNet op wilt pakken
    </p>
    <div class="grid grid-cols-1 gap-2 ">
        <div class="rounded-xl border border-slate-200 bg-[#CDB6E2]/10 p-3">
            <p class="mb-1 text-[10px] font-black uppercase text-slate-600">Basiskennis</p>
            <p class="text-[9px] italic leading-tight text-slate-600">
                Ik heb dit onderwerp meerdere keren in de praktijk begeleid. Vanuit mijn algemene kennis ben ik in staat
                om lichte problematiek zelfstandig op te pakken. Hoewel ik voor dit specifieke gebied geen gerichte
                scholing heb gevolgd, kan ik de basisbeginselen effectief toepassen.
            </p>
        </div>
        <div class="rounded-xl border border-blue-100 bg-[#CDB6E2]/10 p-3">
            <p class="mb-1 text-[10px] font-black uppercase text-[#7C46AF]">Gerichte kennis</p>
            <p class="text-[9px] italic leading-tight text-[#7C46AF] ">
                Ik beschik over aantoonbare werkervaring op dit gebied en dit thema komt regelmatig terug in mijn
                caseload. Ik ben bekwaam in het behandelen van matig complexe casuïstiek. Daarnaast heb ik mijn
                praktijkervaring versterkt door het volgen van aanvullende scholing.
            </p>
        </div>
        <div class="rounded-xl border border-indigo-100 bg-[#B999D6]/10 p-3">
            <p class="mb-1 text-[10px] font-black uppercase text-[#613689]">Expert/specialist</p>
            <p class="text-[9px] italic leading-tight text-[#613689]">
                Ik bezit diepgaande kennis van dit onderwerp en hanteer een structurele focus in mijn werkzaamheden. Ik
                ben gewend om met complexe casuïstiek om te gaan en fungeer voor collega's als vraagbaak en supervisor.
                Mijn expertise wordt ondersteund door een relevante verdiepende opleiding of certificering.
            </p>
        </div>
    </div>


    <div class="group relative flex items-center border gap-3 px-3 py-3 border-solid rounded-2xl border-[#CDB6E2] focus-within:border-[#7C46AF]">
        <Search size={18} color="#7C46AF"/>
        <input
                type="text"
                placeholder="Zoek op thema..."
                class="w-full text-sm outline-none"
                bind:value={searchTerm}
        />
    </div>

    <div class="custom-scrollbar max-h-80 space-y-6 overflow-y-auto pr-2">
        {#each Object.entries($FormAttributesStore.data.categories) as [categoryName, themes]}
            {@const filteredThemes = themes.filter(
                // @ts-ignore
                (theme) => theme.toLowerCase().includes(searchTerm.toLowerCase())
            )}
            {#if filteredThemes.length > 0}
                <div class="space-y-2">
                    <div class="mb-3 flex items-start flex-col justify-between border-solid border-b border-slate-100 pb-1">
                        <h3 class="text-[10px] font-black uppercase tracking-[0.2em] text-[#7C46AF]">{categoryName}</h3>
                        {#if categoryName.includes('CAT 4')}
              <span
                      class="flex items-center gap-1 rounded-full bg-amber-50 px-2 py-0.5 text-[8px] font-bold uppercase text-amber-600"
              >
                <AlertCircle size={10}/> Inzet op deze thema's zal voornamelijk als overbrugging naar S-GGZ plaatsvinden.
              </span>
                        {/if}
                    </div>

                    <div class="grid grid-cols-1 gap-2">
                        {#each filteredThemes as theme}
                            {@const
                                canHaveExpert = !categoryName.includes('CAT 1') || theme === 'Loopbaan- en ontwikkelvraagstukken'}
                            <div
                                    class={`rounded-2xl border border-solid p-3 transition-all ${formData.expertise.includes(theme)
                  ? 'border-[#B999D6] bg-white shadow-sm'
                  : 'border-transparent bg-slate-50/50 hover:bg-slate-50'}`}
                            >
                                <Checkbox
                                        checked={formData.expertise.includes(theme)}
                                        onchange={() => {
                                            const wasSelected = formData.expertise.includes(theme);
                                            toggleList('expertise', theme)
                                            if (theme === OTHER_EXPERTISE && wasSelected) {
                                                updateField('otherExpertiseText', '');
                                            }
                                            if (formData.top3Expertise.includes(theme)) {
                                                toggleList('top3Expertise', theme)
                                            }
                                        }}
                                        label={theme}
                                />
                                {#if formData.expertise.includes(theme)}
                                    <div class="animate-in slide-in-from-top-1 mt-3 pl-7"
                                         transition:slide|local={{axis:'y', duration: 300}}
                                    >
                                        {#if theme === OTHER_EXPERTISE}
                                            <div class="mb-3">
                                                <Input
                                                        type="text"
                                                        placeholder="Vul hier een extra thematiek in..."
                                                        bind:value={formData.otherExpertiseText}
                                                />
                                            </div>
                                        {/if}
                                        <div class="grid grid-cols-3 gap-1">
                                            {#each levels as level}
                                                {#if level !== 'EXPERT' || canHaveExpert}
                                                    <Button
                                                            size="sm"
                                                            onclick={() => updateField('expertiseLevels', {
                                                          ...formData.expertiseLevels,
                                                          [theme]: level,
                                                        })}
                                                            active={formData.expertiseLevels[theme] === level}
                                                    >
                                                        {level}
                                                    </Button>
                                                {/if}
                                            {/each}
                                        </div>

                                        {#if formData.expertiseLevels[theme] === 'EXPERT'}
                                            <div
                                                    class="animate-in slide-in-from-left-2 mt-3 space-y-2 rounded-xl border border-[#B999D6] bg-[#CDB5E2]/10 p-3"
                                            >
                                                <input
                                                        type="text"
                                                        placeholder="Welke verdiepende opleiding of certificering heb je hiervoor gevolgd?"
                                                        class="w-full rounded-lg border bg-white p-2 text-[10px] outline-none placeholder-slate-600"
                                                        value={formData.expertiseEducation[theme] ?? ''}
                                                        oninput={(event) =>
                            updateField('expertiseEducation', {
                              ...formData.expertiseEducation,
                              [theme]: readInputValue(event),
                            })}
                                                />
                                                <input
                                                        type="number"
                                                        placeholder="Hoeveel jaar ervaring heb je specifiek op dit expert-gebied?"
                                                        class="w-full rounded-lg border bg-white p-2 text-[10px] outline-none focus:ring-1 placeholder-slate-600"
                                                        value={formData.expertiseYears[theme] ?? ''}
                                                        oninput={(event) =>
                            updateField('expertiseYears', {
                              ...formData.expertiseYears,
                              [theme]: readInputValue(event),
                            })}
                                                />
                                            </div>
                                        {/if}
                                    </div>
                                {/if}
                            </div>
                        {/each}
                    </div>
                </div>
            {/if}
        {/each}
    </div>

    <div class="space-y-2 rounded-2xl border border-dashed border-[#CDB6E2] bg-[#CDB6E2]/10 p-3">
        <Checkbox
                checked={formData.expertise.includes(OTHER_EXPERTISE)}
                onchange={() => {
                    const wasSelected = formData.expertise.includes(OTHER_EXPERTISE);
                    toggleList('expertise', OTHER_EXPERTISE)
                    if (wasSelected) {
                        updateField('otherExpertiseText', '');
                    }
                    if (formData.top3Expertise.includes(OTHER_EXPERTISE)) {
                        toggleList('top3Expertise', OTHER_EXPERTISE)
                    }
                }}
                label={OTHER_EXPERTISE}
        />

        {#if formData.expertise.includes(OTHER_EXPERTISE)}
            <div class="animate-in slide-in-from-top-1 mt-3 space-y-3 pl-7"
                 transition:slide|local={{axis:'y', duration: 300}}
            >
                <Input
                        type="text"
                        placeholder="Vul hier een extra thematiek in..."
                        bind:value={formData.otherExpertiseText}
                />
                <div class="grid grid-cols-3 gap-1 mt-4">
                    {#each levels as level}
                        <Button
                                size="sm"
                                onclick={() => updateField('expertiseLevels', {
                                  ...formData.expertiseLevels,
                                  [OTHER_EXPERTISE]: level,
                                })}
                                active={formData.expertiseLevels[OTHER_EXPERTISE] === level}
                        >
                            {level}
                        </Button>
                    {/each}
                </div>

                {#if formData.expertiseLevels[OTHER_EXPERTISE] === 'EXPERT'}
                    <div
                            class="animate-in slide-in-from-left-2 mt-3 space-y-2 rounded-xl border border-[#B999D6] bg-[#CDB5E2]/10 p-3"
                    >
                        <input
                                type="text"
                                placeholder="Welke verdiepende opleiding of certificering heb je hiervoor gevolgd?"
                                class="w-full rounded-lg border bg-white p-2 text-[10px] outline-none placeholder-slate-600"
                                value={formData.expertiseEducation[OTHER_EXPERTISE] ?? ''}
                                oninput={(event) =>
                        updateField('expertiseEducation', {
                          ...formData.expertiseEducation,
                          [OTHER_EXPERTISE]: readInputValue(event),
                        })}
                        />
                        <input
                                type="number"
                                placeholder="Hoeveel jaar ervaring heb je specifiek op dit expert-gebied?"
                                class="w-full rounded-lg border bg-white p-2 text-[10px] outline-none focus:ring-1 placeholder-slate-600"
                                value={formData.expertiseYears[OTHER_EXPERTISE] ?? ''}
                                oninput={(event) =>
                        updateField('expertiseYears', {
                          ...formData.expertiseYears,
                          [OTHER_EXPERTISE]: readInputValue(event),
                        })}
                        />
                    </div>
                {/if}
            </div>
        {/if}
    </div>

    {#if formData.expertise.length > 0}
        <div class="mt-6 space-y-3 rounded-[2rem] bg-[#613689] p-5 text-white shadow-xl"
             transition:slide={{axis:'y', duration: 300}}
        >
            <p class="flex items-center gap-2 text-xs font-bold">
                <Star size={16}/>
                Selecteer uw top 3 specialismen:
            </p>
            <div class="flex flex-wrap gap-2">
                {#each formData.expertise as theme}
                    <button
                            transition:slide|local={{axis:'y', duration: 300}}
                            type="button"
                            onclick={() => toggleList('top3Expertise', theme, 3)}
                            class={`rounded-full cursor-pointer text-left border border-solid px-3 py-1.5 text-[10px] font-bold transition-all ${formData.top3Expertise.includes(
              theme,
            )
              ? 'border-white bg-white text-[#613689]'
              : 'border-[#A57CCB] bg-[#613689] text-white hover:border-[#CDB6E2]'}`}
                    >
                        {getExpertiseLabel(theme)}
                    </button>
                {/each}
            </div>
        </div>
    {/if}
</div>

<style>
    @import "tailwindcss";
</style>
